import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import { Sparkles, CheckCircle2, Star, Banknote, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { API } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

export default function PackagesSection({ ctaPath = "/register?role=parent" }) {
  const { t } = useTranslation();
  const fmt = useFmtXof();
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    axios.get(`${API}/legal/packages`).then(({ data }) => setPackages(data.items || [])).catch(() => {});
  }, []);

  return (
    <section className="py-24 bg-[#F3F7F5]" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="pill"><Banknote className="h-3.5 w-3.5" /> {t("packages.pill")}</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">{t("packages.title")}</h2>
          <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">
            <Trans i18nKey="packages.subtitle">
              <strong className="text-[#0A2518]">placeholder</strong>
            </Trans>
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-testid="packages-grid">
          {packages.map((p) => {
            const mid = p.tiers?.[1];
            const max = p.tiers?.[2];
            return (
              <Card
                key={p.id}
                className={`relative p-8 border-[#E5EBE7] rounded-3xl ${p.popular ? "border-[#16A34A]/60 shadow-xl ring-2 ring-[#16A34A]/20" : ""}`}
                data-testid={`package-${p.id}`}
              >
                {p.popular && (
                  <Badge className="absolute -top-3 right-6 bg-[#16A34A] text-white border-0 px-3 py-1">
                    <Star className="h-3 w-3 mr-1 fill-white" /> {t("packages.popular")}
                  </Badge>
                )}
                <h3 className="font-display text-3xl font-bold text-[#0A2518]">{p.name}</h3>
                <p className="mt-2 text-[#4B5563]">{p.description}</p>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold text-[#15803D]" data-testid={`package-price-${p.id}`}>
                    {fmt(mid?.total_xof)}
                  </span>
                  <span className="text-[#4B5563]">{t("packages.perMonth")}</span>
                </div>
                <p className="text-xs text-[#4B5563] mt-1">
                  {t("packages.priceFootnote", { min: fmt(p.tiers?.[0]?.total_xof), max: fmt(max?.total_xof) })}
                </p>

                <ul className="mt-6 space-y-3 text-sm text-[#0A2518]">
                  <Feature text={t("packages.features.hours", { hours_per_month: p.hours_per_month, hours_per_week: p.hours_per_week })} />
                  <Feature text={t("packages.features.verified")} />
                  <Feature text={t("packages.features.ai")} />
                  <Feature text={t("packages.features.followup")} />
                  <Feature text={t("packages.features.replacement")} />
                  <Feature text={t("packages.features.secure")} />
                </ul>

                <Button asChild className={`mt-8 w-full rounded-full h-12 ${p.popular ? "bg-[#16A34A] hover:bg-[#15803D] text-white" : "bg-white border border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"}`} data-testid={`package-cta-${p.id}`}>
                  <Link to={ctaPath}>{t("packages.cta")} <ChevronRight className="h-4 w-4 ml-1" /></Link>
                </Button>
              </Card>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs text-[#4B5563]">
          <Sparkles className="h-3.5 w-3.5 inline mr-1 text-[#16A34A]" />
          {t("packages.commissionNote")}
        </div>
      </div>
    </section>
  );
}

const Feature = ({ text }) => (
  <li className="flex items-start gap-2">
    <CheckCircle2 className="h-4 w-4 text-[#16A34A] mt-0.5 shrink-0" />
    <span>{text}</span>
  </li>
);
